<template>
  <div class="navigation-tiers">
    <p
      :id="dialogLabel"
      class="visually-hidden"
      v-text="tier?.title"
    />

    <div class="container grid">
      <div class="col xs-span l3">
        <div
          v-if="sidebarLinks.length"
          class="navigation-tiers__sidebar"
        >
          <ul class="navigation-tiers__sidebar-links">
            <template
              v-for="(link, index) of sidebarLinks"
              :key="link.handle"
            >
              <li class="navigation-tiers__sidebar-item">
                <button
                  v-if="link.links.length"
                  ref="sidebarItems"
                  class="
                    navigation-tiers__sidebar-link
                    navigation-tiers__sidebar-link--button
                  "
                  :class="{
                    [$class('active')]: isActiveTab(link),
                  }"
                  :style="{
                    color: getLinkSpecialColor(link),
                  }"
                  :tabindex="getTabTabindex(link)"
                  type="button"
                  @blur="handleNavBlur"
                  @focus="handleNavOpen(link)"
                  @keydown="handleTabKeydown($event, index)"
                  @mouseenter="handleNavOpen(link)"
                  @mouseleave="handleNavBlur"
                >
                  <span
                    class="text-body-s-regular text-left"
                    v-text="link.title"
                  />

                  <icon-chevron-right />
                </button>

                <a
                  v-else
                  ref="sidebarItems"
                  class="
                    navigation-tiers__sidebar-link
                    navigation-tiers__sidebar-link--anchor
                  "
                  :style="{
                    color: getLinkSpecialColor(link),
                  }"
                  :href="link.url"
                  :tabindex="getTabTabindex(link)"
                  @keydown="handleTabKeydown($event, index)"
                  @mouseenter="selectedTier = link"
                  @focus="selectedTier = link"
                >
                  <span
                    class="text-body-s-regular text-left"
                    v-text="link.title"
                  />
                </a>
              </li>
            </template>
          </ul>
        </div>
      </div>

      <div
        ref="content"
        class="navigation-tiers__content col xs-span l6"
      >
        <div
          v-if="linkColumns.length"
          class="navigation-tiers__columns grid"
        >
          <ul
            v-for="(column, index) of linkColumns"
            :key="`navigation-column-${index}`"
            class="navigation-tiers__column col xs-span l4"
          >
            <li
              v-for="link of column.links"
              :key="link.handle"
            >
              <p
                v-if="isValidHeading(link.title)"
                class="navigation-tiers__column-heading text-body-xs-bold"
                v-text="getHeadingText(link.title)"
              />

              <a
                v-else-if="isValidCta(link.title)"
                class="navigation-tiers__column-cta text-body-s-underline"
                :href="link.url"
                v-text="getCtaText(link.title)"
              />

              <a
                v-else-if="isValidLink(link)"
                class="navigation-tiers__column-link text-body-s-regular"
                :href="link.url"
                v-text="link.title"
              />
            </li>
          </ul>
        </div>
      </div>

      <div
        ref="promotions"
        class="navigation-tiers__promotions col xs-span l3"
      >
        <a
          v-for="promotion of promotions"
          :key="promotion.handle"
          class="navigation-tiers__promotion"
          :href="promotion.url"
        >
          <responsive-image
            class="navigation-tiers__promotion-image"
            :image="{
              src: promotion.image,
            }"
            ratio="4:1"
            sizes="25vw"
          />

          <div class="navigation-tiers__promotion-overlay" />

          <p
            class="
              navigation-tiers__promotion-title
              text-body-xs-bold
            "
          >
            <span v-text="promotion.title" />
            <icon-chevron-right />
          </p>
        </a>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Navigation tiers (navigation-tiers)
 * -----------------------------------------------------------------------------
 * Handles the tier structures for the desktop handles.
 *
 * @param {Object} overlay - Overlay API.
 * @param {Object} tier - Menu item for mega menu.
 *
 */
import { mapActions } from 'vuex'

import ResponsiveImage from '~global/images/responsive-image'

import IconChevronRight from '~icons/directional-navigation/chevron/right.svg'

import { specialLinks } from '~/config/navigations'
import { convertToHandle } from '~/helpers/convert'
import { validateNavigationMenuTitle } from '~/helpers/vue'

export default {
  name: 'NavigationTiers',

  components: {
    IconChevronRight,
    ResponsiveImage,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
    tier: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      linkColumns: [],
      navDelay: this.$timing('quick'),
      navOpenTimeout: false,
      promotions: [],
      selectedTier: null,
      sidebarLinks: [],
    }
  },

  watch: {

    /**
     * Watch isActive to trap focus.
     */
    'overlay.isActive'() {
      this.updateFocusTrap()
    },

    /**
     * Init component after tier changed.
     */
    tier: {
      handler() {
        if (!this.tier) {
          this.sidebarLinks = []
          return
        }

        this.init()
      },
      deep: true,
    },

    /**
     * Init component after selected tier changed.
     */
    selectedTier: {
      handler() {
        this.buildLinkColumns()
        this.buildPromotions()
        this.updateFocusTrap()
      },
      deep: true,
    },
  },

  computed: {

    /**
     * Get dialog label.
     * - For navigation tiers overlay `aria-labelledby`.
     * @returns {String}
     */
    dialogLabel() {
      return this.tier?.title
        ? `overlay-${this.overlay.namespace}-${convertToHandle(this.tier.title)}`
        : ''
    },
  },

  mounted() {
    this.init()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      setFocusTrap: 'accessibility/setFocusTrap',
    }),

    /**
     * Initialize component.
     */
    init() {
      this.sidebarLinks = this.tier.links.filter((link) => {
        return this.isValidLink(link)
      })
      this.selectedTier = this.sidebarLinks.length
        ? this.sidebarLinks[0]
        : null

      if (!this.selectedTier) {
        this.linkColumns = []
        this.buildPromotions()
        this.updateFocusTrap()
        return
      }

      this.buildLinkColumns()
      this.buildPromotions()
      this.updateFocusTrap()
    },

    /**
     * Build link columns from selected child tier.
     */
    buildLinkColumns() {
      this.linkColumns = []

      if (!this.selectedTier) {
        return
      }

      let currentColumn = -1
      this.selectedTier.links.forEach((link) => {
        const isCol = link.title.includes(specialLinks.col)
        if (!this.linkColumns.length && !isCol) {
          return
        }

        if (isCol) {
          currentColumn++
          this.linkColumns.push({ links: [] })
          return
        }

        this.linkColumns[currentColumn].links.push(link)
      })

      if (this.linkColumns.length) {
        return
      }

      const links = this.selectedTier.links.filter((link) => {
        return this.isValidLink(link) || this.isValidCta(link)
      })
      this.linkColumns.push({ links })
    },

    /**
     * Build promotions from selected child tier.
     */
    buildPromotions() {
      const tier = this.sidebarLinks.length ? this.selectedTier : this.tier
      const promotions = tier.links.filter((link) => {
        return link.title.includes(specialLinks.banner)
      })

      this.promotions = promotions.map((promotion) => {
        const titleArray = promotion.title
          .replace(specialLinks.banner, '')
          .split('|')

        return {
          handle: promotion.handle,
          image: titleArray[0],
          title: titleArray.length > 1 ? titleArray[1] : '',
          url: promotion.url,
        }
      })
    },

    /**
     * Check if the tier has a color inside its links.
     * @param {object} link
     * @return {string}
     */
    getLinkSpecialColor(link) {
      return link.links?.find((item) => {
        return item.title.includes(specialLinks.color)
      })?.title?.replace(specialLinks.color, '')
    },

    /**
     * Get heading text.
     * @param {String} title - Title.
     * @returns {String}
     */
    getHeadingText(title) {
      return title.replace(specialLinks.heading, '')
    },

    /**
     * Get cta text.
     * @param {String} title - Title.
     * @returns {String}
     */
    getCtaText(title) {
      const ctaString = title.replace(specialLinks.showCta, '')
      if (ctaString) {
        return ctaString
      }

      return this.$string('general.site_header.view_all')
    },

    /**
     * Return button tabindex.
     * - Set to null if active because tabindex="0" on a button is redundant.
     * @param {Object} link - Menu item.
     * @returns {Number|null}
     */
    getTabTabindex(link) {
      return this.isActiveTab(link)
        ? null
        : -1
    },

    /**
     * Check if link is valid.
     * @param {String} title
     * @return {boolean}
     */
    isValidLink({ title }) {
      return !validateNavigationMenuTitle(title)
    },

    /**
     * Check if link is valid heading.
     * @param {String} title - Title.
     * @returns {Boolean}
     */
    isValidHeading(title) {
      return title.includes(specialLinks.heading)
    },

    /**
     * Check if link is valid cta.
     * @param {String} title - Title.
     * @returns {Boolean}
     */
    isValidCta(title) {
      return title.includes(specialLinks.showCta)
    },

    /**
     * Return if a given tab is active.
     * @param {Object} link - Menu item.
     * @returns {Boolean}
     */
    isActiveTab(link) {
      return link.handle === this.selectedTier.handle
    },

    /**
     * Handle nav open.
     * @param {Object} link - Menu item.
     */
    handleNavOpen(link) {
      clearTimeout(this.navOpenTimeout)

      this.navOpenTimeout = setTimeout(() => {
        this.selectedTier = link
      }, this.navDelay)
    },

    /**
     * Handle keydown events on tablist buttons.
     * @param {Event} $event - Keydown event.
     * @param {Number} index - Index of current tab.
     */
    handleTabKeydown($event, index) {
      let flag = false
      let targetIndex = index

      switch ($event.key) {
        case 'ArrowUp':
          targetIndex = index - 1 >= 0
            ? index - 1
            : index

          flag = true
          break

        case 'ArrowDown':
          targetIndex = index + 1 < this.sidebarLinks.length
            ? index + 1
            : index

          flag = true
          break

        case 'Home':
          targetIndex = 0
          flag = true
          break

        case 'End':
          targetIndex = this.sidebarLinks.length - 1
          flag = true
          break

        default:
          break
      }

      if (!flag) {
        return
      }

      $event.stopPropagation()
      $event.preventDefault()
      this.selectedTier = this.sidebarLinks[targetIndex]
      this.$refs.sidebarItems[targetIndex].focus()
    },

    /**
     * Handle navigation blur.
     */
    handleNavBlur() {
      clearTimeout(this.navOpenTimeout)
    },

    /**
     * Update focus trap.
     */
    async updateFocusTrap() {
      await this.$nextTick()

      if (
        !this.overlay.isActive ||
        !this.$refs.sidebarItems ||
        !this.$refs.sidebarItems.length
      ) {
        this.clearFocusTrap()
        return
      }

      const activeLink = this.$refs.sidebarItems.find((link) => {
        return link.classList.contains(this.$class('active'))
      })

      const focusContainers = [
        this.$refs.content,
        this.$refs.promotions,
      ]

      if (activeLink) {
        focusContainers.push(activeLink.parentElement)
        activeLink.focus()
      }

      this.setFocusTrap({
        container: focusContainers,
      })
    },
  },
}
</script>
